import { Checkbox } from "@xola/ui-kit";
import clsx from "clsx";
import { now } from "common";
import { isEmpty } from "lodash";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Event, Experience, Permission, Timeslot } from "types";
import shallow from "zustand/shallow";
import { EventApi } from "@fullcalendar/common";
import { useRosterDrawerStore } from "../../stores/rosterDrawerStore";
import { IsGranted } from "../Common/IsGranted";
import { normalizeTimeslotId } from "./Calendar/DashboardCalendar";
import { DashboardCalendarItemDetails } from "./Calendar/DashboardCalendarItemContent";
import {
    CalendarItemTooltipHeading,
    CheckedInAndVouchersGrouped,
    DashboardCalendarItemTitle,
    SubInfoGrouped,
} from "./Calendar/DashboardCalendarItemResponsiveComponents";
import {
    shouldShowBalance,
    shouldShowBalanceInFirstRow,
    shouldShowBalanceTooltip,
    shouldShowCheckedInAndVouchersSeparate,
    shouldShowExperienceInLastRow,
    shouldShowName,
    shouldShowSubInfoSeparate,
} from "./Calendar/dashboardResponsiveHelpers";
import { getCalendarColors } from "./Colors/colorHelpers";
import { BalanceBadge } from "./Common/BalanceBadge";
import { BlackoutTimeslotTooltip } from "./Common/BlackoutTimeslotTooltip";
import { DashboardItemActionButtons } from "./DashboardItemActionButtons";
import { DashboardPlaceholderItem } from "./DashboardPlaceholderItem";
import { updateTimeslotsCollectionWithPlaceholder } from "./helper";
import { useExperience } from "@/hooks/resources/useProduct";
import { canOpenRoster } from "@/models/Timeslot";
import "./Calendar/timegrid.css";

interface DashboardDayItemProps {
    event: EventApi;
    isSelected: boolean;
    toggleEventSelection: (event: EventApi) => void;
}

export const DashboardDayItem = ({ event: fcEvent, isSelected, toggleEventSelection }: DashboardDayItemProps) => {
    const previousWidth = useRef(0);
    const [width, setWidth] = useState(0);
    const [height, setHeight] = useState(0);
    const dayItemComponentRef = useRef(null);

    // Experience can be undefined in some cases.
    const {
        timeslot,
        experience: fcEventExperience,
        event,
    }: { timeslot: Timeslot; experience?: Experience; event: Event } = fcEvent.extendedProps as any;

    const { title: fcEventTitle } = fcEvent;

    const { aggregatedBalance, arrivalDatetime, product } = timeslot;

    // Get the experience data in case it's undefined in fcEvent.
    const { experience: experienceData } = useExperience(product, null, {
        enabled: !fcEventExperience || !fcEventTitle,
    });
    const experience = fcEventExperience ?? experienceData;

    // In cases when the experience is undefined, the title comes as "undefined" string
    const title = fcEventTitle && fcEventTitle !== "undefined" ? fcEventTitle : experience?.name;

    const handleBeforePlaceholderClick = async () => {
        if (timeslot.placeholder) {
            updateTimeslotsCollectionWithPlaceholder(timeslot, timeslot.placeholder, "before");
        }
    };

    const handleAfterPlaceholderClick = async () => {
        if (timeslot.placeholder) {
            updateTimeslotsCollectionWithPlaceholder(timeslot, timeslot.placeholder, "after");
        }
    };

    // This gets the styles for the tile based on the dashboard color preferences
    const styles = useMemo(() => getCalendarColors(timeslot, experience?.id), [timeslot, experience?.id]);

    const isPastEvent = now() > arrivalDatetime;

    const [setDrawerOpen, setDrawerTimeslot] = useRosterDrawerStore(
        (state) => [state.setOpen, state.setTimeslot],
        shallow,
    );

    const isRosterAvailable = canOpenRoster(timeslot);

    const handleClick = (e: any) => {
        if (e.ctrlKey || e.metaKey) {
            toggleEventSelection(fcEvent);
        } else if (isRosterAvailable) {
            setDrawerTimeslot(timeslot);
            setDrawerOpen();
        }
    };

    useEffect(() => {
        const updateComponentSize = () => {
            if (dayItemComponentRef.current) {
                const { clientWidth, clientHeight } = dayItemComponentRef.current;
                if (clientWidth !== previousWidth.current) {
                    setWidth(clientWidth);
                    previousWidth.current = clientWidth;
                }

                setHeight(clientHeight);
            }
        };

        // Call the function to update size initially
        updateComponentSize();

        // Update size on window resize
        window.addEventListener("resize", updateComponentSize);

        return () => {
            window.removeEventListener("resize", updateComponentSize);
        };
    }, [width]);

    return (
        <div className="h-full w-full" id={normalizeTimeslotId(timeslot.id)}>
            {!isEmpty(timeslot.placeholder?.before) && (
                <DashboardPlaceholderItem onClick={handleBeforePlaceholderClick} />
            )}
            <div data-event-id={event?.id} className={clsx("ui-dashboard-item flex h-full w-full")}>
                <BlackoutTimeslotTooltip
                    reason={isRosterAvailable ? undefined : timeslot.unavailability?.message}
                    heading={<CalendarItemTooltipHeading timeslot={timeslot} title={title} />}
                >
                    <div
                        ref={dayItemComponentRef}
                        className={clsx(
                            "mx-2 flex h-full w-full items-start justify-between rounded-md border-l-2 px-2 py-2 text-black",
                            isRosterAvailable ? "hover:cursor-pointer" : "hover:cursor-not-allowed",
                            { "border-l-0 opacity-80 grayscale filter ": isPastEvent },
                            isSelected ? "!bg-primary" : "bg-gray-lighter",
                        )}
                        style={isPastEvent ? undefined : styles}
                        onClick={handleClick}
                    >
                        <div className="flex h-fit flex-grow items-start justify-end">
                            {isSelected && (
                                <div className="mr-2">
                                    <Checkbox checked={isSelected} onClick={() => toggleEventSelection(fcEvent)} />
                                </div>
                            )}

                            <div className="flex h-fit w-full flex-col items-start space-y-2">
                                {/* First Row with Timeslot info */}
                                <div className="flex h-fit w-full items-center space-x-1">
                                    <DashboardCalendarItemTitle fcEvent={fcEvent} width={width} height={height} />
                                    <DashboardCalendarItemDetails
                                        experience={experience}
                                        fcEvent={fcEvent}
                                        width={width}
                                        height={height}
                                    />
                                </div>

                                {/* Second Row with Timeslot info */}
                                {shouldShowSubInfoSeparate(height, width) && (
                                    <SubInfoGrouped event={event} timeslot={timeslot} />
                                )}

                                {shouldShowCheckedInAndVouchersSeparate(height, width) && (
                                    <CheckedInAndVouchersGrouped timeslot={timeslot} experience={experience} />
                                )}

                                {/* Third Row with Timeslot info */}
                                {shouldShowExperienceInLastRow(height) && shouldShowName(height, width) && (
                                    <span className="mx-2 overflow-hidden text-ellipsis font-bold">{title}</span>
                                )}

                                {aggregatedBalance > 0 && (
                                    <IsGranted permissions={Permission.AMOUNT_DUE_VIEW}>
                                        {!shouldShowBalanceInFirstRow(height) && shouldShowBalance(width) && (
                                            <div className="flex w-full justify-end">
                                                <BalanceBadge
                                                    className="max-w-fit"
                                                    value={aggregatedBalance}
                                                    showValueInTooltip={shouldShowBalanceTooltip(height, width)}
                                                />
                                            </div>
                                        )}
                                    </IsGranted>
                                )}
                            </div>

                            {/* ellipses & book button */}
                            <div className="ml-4 flex justify-end">
                                <DashboardItemActionButtons size="tiny" timeslot={timeslot} />
                            </div>
                            {!isEmpty(timeslot.placeholder?.after) && (
                                <DashboardPlaceholderItem onClick={handleAfterPlaceholderClick} />
                            )}
                        </div>
                    </div>
                </BlackoutTimeslotTooltip>
            </div>
        </div>
    );
};
